import ReactDOM from 'react-dom';
import './BottomSheet.css'
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { pushBackHandler } from '../../hooks/backHandlerStack';

interface BottomSheetProps {
    children: React.ReactNode
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
}

export const BottomSheet = ({children, isOpen, setIsOpen} : BottomSheetProps) => {
    const portalRoot = document.getElementById("portal");
    const [isVisible, setIsVisible] = useState(false);
    const scrimRef = useRef<HTMLDivElement>(null);
    const bottomSheetRef = useRef<HTMLDivElement>(null);

    const openTimer = useRef<NodeJS.Timer | undefined>(undefined);
    const closeTimer = useRef<NodeJS.Timer | undefined>(undefined);

    // 키보드 영역만큼 sheet 를 위로 올리고, 가용 높이를 CSS 변수로 노출.
    //   - keyboardOffset: layout(window.innerHeight) - visual(키보드 위) - offsetTop = 키보드 높이.
    //     sheet 의 `bottom` 에 적용해 키보드 위로 올림.
    //   - vvHeight: visualViewport.height — 자식 content 가 `--bs-vv-height` 로 받아 max-height 제약 가능.
    //     (viewport meta 가 `interactive-widget=overlays-content` 라 dvh 가 키보드에 반응 안 함 → JS 가 직접 전달)
    //   - visualViewport 미지원 환경: 두 값 모두 기본 (offset=0, vvHeight=null) → 기존 동작과 동일.
    //   - sheet 가 안 열린 동안엔 listener 자체를 attach 하지 않아 비용 zero.
    const [keyboardOffset, setKeyboardOffset] = useState(0);
    const [vvHeight, setVvHeight] = useState<number | null>(null);
    useEffect(() => {
        if (!isVisible || typeof window === "undefined" || !window.visualViewport) return;
        const vv = window.visualViewport;
        const update = () => {
            const offset = window.innerHeight - vv.height - vv.offsetTop;
            setKeyboardOffset(Math.max(0, offset));
            setVvHeight(vv.height);
        };
        update();
        vv.addEventListener("resize", update);
        vv.addEventListener("scroll", update);
        return () => {
            vv.removeEventListener("resize", update);
            vv.removeEventListener("scroll", update);
            setKeyboardOffset(0);
            setVvHeight(null);
        };
    }, [isVisible]);


    const openBottomSheet = () => {
        if(closeTimer.current) clearTimeout(closeTimer.current);

        // iOS WebView에서 키보드가 열린 상태로 BottomSheet를 열면 viewport가 어긋나는 문제 해결
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }

        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                bottomSheetRef.current?.classList.add("open");
                scrimRef.current?.classList.add("open");
            })
        })
        
    }

    const closeBottomSheet = () => {
        if(openTimer.current) clearTimeout(openTimer.current);
        bottomSheetRef.current?.classList.remove("open");
        scrimRef.current?.classList.remove("open");
        closeTimer.current = setTimeout(() => {
            setIsOpen(false);
            setIsVisible(false);       
        }, 300)
    }

    useEffect(() => {
        if(isOpen) {
            setIsVisible(true);
        } else {
            closeBottomSheet();
        }
    },[isOpen])

    useEffect(() => {
        if(isVisible) {
            openBottomSheet();
        }
    },[isVisible])

    // device back 으로도 닫을 수 있게 스택에 close handler push.
    //   다층 dismiss: 키보드(시스템) → 모달(여기서) → 페이지(useBackInterceptor) 순서.
    //   isVisible 동안만 등록 — close animation 완료 후엔 자동으로 pop.
    useEffect(() => {
        if (!isVisible) return;
        return pushBackHandler(() => setIsOpen(false));
    }, [isVisible, setIsOpen]);

    if(!portalRoot || !isVisible) return null;

    return ReactDOM.createPortal(
        <div
            className="bottom-sheet-container"
        >
            <div className="bottom-sheet-scrim"
                onClick={() => {
                    closeBottomSheet();
                }}
                ref={scrimRef}
            />
            <div
                className={"bottom-sheet"}
                ref={bottomSheetRef}
                style={{
                    bottom: keyboardOffset,
                    ...(vvHeight !== null && { ["--bs-vv-height" as any]: `${vvHeight}px` }),
                }}
            >
                {children}
            </div>
        </div>
    , portalRoot)
};